<template>
  <div class="space-y-3">
    <!-- Stage: the video plus the selection overlay. Overlay geometry is in
         physical px (inline styles) because the picture never mirrors in RTL. -->
    <div
      ref="stageRef"
      class="relative mx-auto w-full max-h-[560px] select-none overflow-hidden rounded-lg border bg-background"
      :style="stageStyle"
    >
      <video
        v-if="file"
        ref="videoRef"
        :src="fileUrl"
        muted
        playsinline
        preload="metadata"
        class="block h-full w-full object-contain"
        @loadedmetadata="onLoadedMetadata"
        @loadeddata="requestPreview"
        @timeupdate="onTimeUpdate"
        @seeked="requestPreview"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        @ended="isPlaying = false"
        @error="onVideoError"
      />
      <!-- A link has no local picture: YouTube plays through its embed, any
           other link gets an empty frame the box is still placed on. -->
      <div v-else ref="frameRef" class="absolute inset-0 bg-scrim">
        <div v-if="mode === 'youtube'" ref="youtubeHost" class="h-full w-full" />
        <p v-else class="px-6 pt-[18%] text-center text-sm text-scrim-foreground">
          {{ t("pages.hardsubToSubtitle.regionNoPreview") }}
        </p>
      </div>

      <template v-if="rect && !videoError">
        <!-- Dim everything outside the box -->
        <div
          v-for="(panel, index) in maskPanels"
          :key="index"
          class="pointer-events-none absolute bg-background/60"
          :style="panel"
          aria-hidden="true"
        />
        <div
          ref="boxRef"
          role="group"
          tabindex="0"
          :aria-label="t('pages.hardsubToSubtitle.regionLabel')"
          :aria-describedby="`${uid}-coords`"
          :class="[
            'absolute touch-none border-2 border-primary bg-primary/10 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            disabled ? 'cursor-default' : 'cursor-move',
          ]"
          :style="boxStyle"
          @pointerdown="onPointerDown($event, 'move')"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
          @keydown="onKeydown"
        >
          <span
            v-for="handle in HANDLES"
            :key="handle"
            class="absolute h-3.5 w-3.5 rounded-full border-2 border-primary bg-background [@media(pointer:coarse)]:h-5 [@media(pointer:coarse)]:w-5"
            :style="handleStyle(handle)"
            aria-hidden="true"
            @pointerdown.stop="onPointerDown($event, handle)"
          />
        </div>
      </template>

      <div
        v-if="videoError"
        class="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-muted-foreground"
        role="alert"
      >
        <span class="inline-flex items-start gap-2">
          <Icon name="lucide:circle-alert" class="mt-0.5 h-4 w-4 flex-none text-destructive" aria-hidden="true" />
          <span>{{ t("pages.hardsubToSubtitle.errors.unplayable") }}</span>
        </span>
      </div>
    </div>

    <!-- Announced coordinates, also referenced by the box -->
    <p :id="`${uid}-coords`" class="sr-only" aria-live="polite">{{ coordsText }}</p>

    <!-- Transport: find a frame that shows a subtitle. The timeline reads
         left-to-right like timecodes do, so the row is pinned to ltr. -->
    <div v-if="!videoError && mode !== 'none'" class="space-y-2" dir="ltr">
      <input
        type="range"
        class="block h-2 w-full cursor-pointer accent-primary disabled:cursor-not-allowed"
        :aria-label="t('pages.hardsubToSubtitle.scrubLabel')"
        :aria-valuetext="formatTimecode(currentTime, forceHours)"
        min="0"
        :max="duration || 0"
        step="0.1"
        :value="currentTime"
        :disabled="disabled || duration <= 0"
        @input="onScrub"
      />
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-if="mode === 'file'"
          type="button"
          size="icon"
          variant="outline"
          class="bg-background"
          :disabled="disabled || duration <= 0"
          :aria-label="isPlaying ? t('pages.hardsubToSubtitle.pause') : t('pages.hardsubToSubtitle.play')"
          :title="isPlaying ? t('pages.hardsubToSubtitle.pause') : t('pages.hardsubToSubtitle.play')"
          @click="togglePlay"
        >
          <Icon :name="isPlaying ? 'lucide:pause' : 'lucide:play'" class="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          v-for="step in SEEK_STEPS"
          :key="step"
          type="button"
          size="sm"
          variant="outline"
          class="bg-background font-mono"
          :disabled="disabled || duration <= 0"
          :aria-label="step < 0
            ? t('pages.hardsubToSubtitle.seekBack', { seconds: Math.abs(step) })
            : t('pages.hardsubToSubtitle.seekForward', { seconds: step })"
          @click="seekBy(step)"
        >
          {{ step > 0 ? `+${step}s` : `${step}s` }}
        </Button>
        <span class="font-mono text-xs text-subtle-foreground">
          {{ formatTimecode(currentTime, forceHours) }} / {{ formatTimecode(duration, forceHours) }}
        </span>
        <div class="ms-auto flex items-center gap-1.5" role="group" :aria-label="t('pages.hardsubToSubtitle.scrubLabel')">
          <button
            v-for="percent in JUMP_PERCENTS"
            :key="percent"
            type="button"
            :class="chipButtonClass"
            :disabled="disabled || duration <= 0"
            :aria-label="t('pages.hardsubToSubtitle.jumpTo', { percent })"
            @click="seekTo((duration * percent) / 100)"
          >
            {{ percent }}%
          </button>
        </div>
      </div>
    </div>

    <!-- Numeric fallback (keyboard / screen reader) + reset -->
    <div v-if="!videoError" class="flex flex-wrap items-end gap-x-3 gap-y-2">
      <div v-for="field in FIELDS" :key="field.key" class="space-y-1">
        <label :for="`${uid}-${field.key}`" class="block text-xs font-medium text-muted-foreground">
          {{ t(field.label) }}
        </label>
        <Input
          :id="`${uid}-${field.key}`"
          v-model="drafts[field.key]"
          type="number"
          inputmode="numeric"
          min="0"
          max="100"
          step="1"
          dir="ltr"
          class="w-20 bg-background text-center font-mono"
          :disabled="disabled"
          @blur="commitDraft(field.key)"
          @keydown.enter.prevent="commitDraft(field.key)"
        />
      </div>
      <Button type="button" variant="soft" size="sm" class="gap-1.5" :disabled="disabled" @click="resetRegion">
        <Icon name="lucide:rotate-ccw" class="h-3.5 w-3.5" aria-hidden="true" />
        <span>{{ t(orientation === "vertical" ? "pages.hardsubToSubtitle.regionResetVertical" : "pages.hardsubToSubtitle.regionReset") }}</span>
      </Button>
      <p class="basis-full text-xs text-subtle-foreground">{{ t("pages.hardsubToSubtitle.regionKeyboardHint") }}</p>
    </div>

    <!-- Crop preview: exactly the pixels the OCR will read. A cross-origin
         player can't be drawn to a canvas, so only local files get one. -->
    <div v-if="!videoError && mode === 'file'" class="space-y-1.5">
      <p class="font-mono text-[11px] uppercase tracking-[0.12em] text-subtle-foreground">
        {{ t("pages.hardsubToSubtitle.regionPreview") }}
      </p>
      <canvas
        ref="canvasRef"
        class="block h-auto w-full rounded-md border bg-background"
        :aria-label="t('pages.hardsubToSubtitle.regionPreview')"
        role="img"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  MIN_REGION_H,
  MIN_REGION_W,
  defaultSubtitleRegion,
  regionToPixels,
  type SubtitleOrientation,
  type SubtitleRegion,
} from "~/utils/hardsub-region";
import { formatTimecode } from "~/utils/clip-range";
import { useYoutubePlayer } from "~/composables/useYoutubePlayer";

const props = withDefaults(
  defineProps<{
    file?: File | null;
    /** Picture source when there is no file. */
    youtubeId?: string;
    /** Link sources don't report a size: 9:16 instead of 16:9. */
    portrait?: boolean;
    modelValue: SubtitleRegion;
    orientation?: SubtitleOrientation;
    disabled?: boolean;
  }>(),
  { file: null, youtubeId: "", portrait: false, orientation: "horizontal", disabled: false }
);

const emit = defineEmits<{
  "update:modelValue": [value: SubtitleRegion];
  /** The video element, so a sibling (clip range) can share the player; null on unmount. */
  media: [element: HTMLVideoElement | null];
  /** Media length once known (seconds). */
  duration: [seconds: number];
  /** "UNPLAYABLE" when the browser can't decode the file, "NO_VIDEO_TRACK" when it has no picture. */
  error: [code: string];
}>();

const { t } = useI18n();
const uid = useId();

type Handle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";
type DragMode = "move" | Handle;
const HANDLES: Handle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
const SEEK_STEPS = [-10, -1, 1, 10] as const;
const JUMP_PERCENTS = [10, 50, 90] as const;
const FIELDS = [
  { key: "x", label: "pages.hardsubToSubtitle.regionLeft" },
  { key: "y", label: "pages.hardsubToSubtitle.regionTop" },
  { key: "w", label: "pages.hardsubToSubtitle.regionWidth" },
  { key: "h", label: "pages.hardsubToSubtitle.regionHeight" },
] as const;
type FieldKey = (typeof FIELDS)[number]["key"];
/** Longest edge of the preview canvas; the OCR itself reads the full-size crop. */
const PREVIEW_MAX_WIDTH = 960;

const chipButtonClass =
  "rounded-full border bg-muted px-2.5 py-1 font-mono text-xs font-semibold tracking-wide text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:pointer-events-none disabled:opacity-55 [@media(pointer:coarse)]:min-h-11";

const stageRef = ref<HTMLElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
const frameRef = ref<HTMLElement | null>(null);
const youtubeHost = ref<HTMLElement | null>(null);
const boxRef = ref<HTMLElement | null>(null);
const canvasRef = ref<HTMLCanvasElement | null>(null);

const fileUrl = ref("");
const videoError = ref(false);
const videoSize = ref({ w: 0, h: 0 });
const duration = ref(0);
const currentTime = ref(0);
const isPlaying = ref(false);
const forceHours = computed(() => duration.value >= 3600);

const youtubeVideoId = computed(() => (props.file ? "" : props.youtubeId));
const youtube = useYoutubePlayer(youtubeHost, youtubeVideoId);
const mode = computed<"file" | "youtube" | "none">(() => {
  if (props.file) return "file";
  return props.youtubeId && !youtube.error.value ? "youtube" : "none";
});

// --- Object URL lifecycle ------------------------------------------------

watch(
  [() => props.file, () => props.youtubeId, () => props.portrait],
  () => {
    if (fileUrl.value) URL.revokeObjectURL(fileUrl.value);
    fileUrl.value = import.meta.client && props.file ? URL.createObjectURL(props.file) : "";
    videoError.value = false;
    videoSize.value = props.file ? { w: 0, h: 0 } : { w: props.portrait ? 9 : 16, h: props.portrait ? 16 : 9 };
    duration.value = 0;
    currentTime.value = 0;
    isPlaying.value = false;
    // Resetting videoSize also clears the overlay rect (see the measure watch).
  },
  { immediate: true }
);

onMounted(() => {
  emit("media", videoRef.value);
  if (stageRef.value && typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => measure());
    resizeObserver.observe(stageRef.value);
  }
});

onBeforeUnmount(() => {
  emit("media", null);
  stopYoutubeClock();
  resizeObserver?.disconnect();
  resizeObserver = null;
  if (previewFrame) cancelAnimationFrame(previewFrame);
  if (fileUrl.value) URL.revokeObjectURL(fileUrl.value);
});

// --- YouTube clock ---------------------------------------------------------

// The embed fires no timeupdate, so the slider follows it on a timer.
const YOUTUBE_CLOCK_MS = 500;
let youtubeClock: ReturnType<typeof setInterval> | null = null;
const stopYoutubeClock = () => {
  if (youtubeClock) clearInterval(youtubeClock);
  youtubeClock = null;
};

watch(youtube.ready, (ready) => {
  stopYoutubeClock();
  if (ready) {
    youtubeClock = setInterval(() => {
      currentTime.value = youtube.getCurrentTime();
    }, YOUTUBE_CLOCK_MS);
  }
});

watch(youtube.duration, (seconds) => {
  if (props.file) return;
  duration.value = seconds;
  if (seconds > 0) emit("duration", seconds);
});

// --- Video events ----------------------------------------------------------

const onLoadedMetadata = () => {
  const video = videoRef.value;
  if (!video) return;
  videoSize.value = { w: video.videoWidth, h: video.videoHeight };
  const seconds = Number.isFinite(video.duration) ? video.duration : 0;
  duration.value = seconds;
  if (seconds > 0) emit("duration", seconds);
  if (!video.videoWidth || !video.videoHeight) {
    videoError.value = true;
    emit("error", "NO_VIDEO_TRACK");
    return;
  }
  // preload="metadata" doesn't guarantee a decoded frame; a tiny seek does.
  try {
    video.currentTime = Math.min(0.05, seconds || 0);
  } catch {
    // Not seekable yet; the first play/scrub will draw the preview.
  }
  nextTick(measure);
};

const onVideoError = () => {
  videoError.value = true;
  rect.value = null;
  emit("error", "UNPLAYABLE");
};

const onTimeUpdate = () => {
  currentTime.value = videoRef.value?.currentTime || 0;
  requestPreview();
};

// --- Transport -------------------------------------------------------------

const seekTo = (seconds: number) => {
  if (duration.value <= 0) return;
  const next = Math.min(Math.max(0, seconds), Math.max(0, duration.value - 0.05));
  if (mode.value === "youtube") {
    youtube.seekTo(next);
  } else if (videoRef.value) {
    videoRef.value.currentTime = next;
  } else {
    return;
  }
  currentTime.value = next;
};

const seekBy = (delta: number) => seekTo(currentTime.value + delta);

const onScrub = (event: Event) => {
  seekTo(Number((event.target as HTMLInputElement).value));
};

const togglePlay = () => {
  const video = videoRef.value;
  if (!video) return;
  if (video.paused) {
    video.play().catch(() => {});
  } else {
    video.pause();
  }
};

// --- Geometry --------------------------------------------------------------

type Rect = { x: number; y: number; w: number; h: number };
/** Where the picture actually sits inside the (object-fit: contain) element. */
const rect = ref<Rect | null>(null);
const stageSize = ref({ w: 0, h: 0 });
let resizeObserver: ResizeObserver | null = null;

const measure = () => {
  const video = videoRef.value || frameRef.value;
  const stage = stageRef.value;
  if (!video || !stage || !videoSize.value.w || !videoSize.value.h) {
    rect.value = null;
    return;
  }
  const elementWidth = video.clientWidth;
  const elementHeight = video.clientHeight;
  if (!elementWidth || !elementHeight) {
    rect.value = null;
    return;
  }
  const scale = Math.min(elementWidth / videoSize.value.w, elementHeight / videoSize.value.h);
  const w = videoSize.value.w * scale;
  const h = videoSize.value.h * scale;
  rect.value = { x: (elementWidth - w) / 2, y: (elementHeight - h) / 2, w, h };
  stageSize.value = { w: stage.clientWidth, h: stage.clientHeight };
};

const stageStyle = computed(() => ({
  aspectRatio: videoSize.value.w && videoSize.value.h ? `${videoSize.value.w} / ${videoSize.value.h}` : "16 / 9",
}));

const px = (n: number) => `${Math.round(n * 100) / 100}px`;

const boxPixels = computed(() => {
  const r = rect.value;
  if (!r) return null;
  const region = props.modelValue;
  return {
    left: r.x + region.x * r.w,
    top: r.y + region.y * r.h,
    width: region.w * r.w,
    height: region.h * r.h,
  };
});

const boxStyle = computed(() => {
  const b = boxPixels.value;
  if (!b) return {};
  return { left: px(b.left), top: px(b.top), width: px(b.width), height: px(b.height) };
});

const maskPanels = computed(() => {
  const b = boxPixels.value;
  if (!b) return [];
  const right = b.left + b.width;
  const bottom = b.top + b.height;
  return [
    { left: "0", top: "0", width: "100%", height: px(b.top) },
    { left: "0", top: px(bottom), width: "100%", bottom: "0" },
    { left: "0", top: px(b.top), width: px(b.left), height: px(b.height) },
    { left: px(right), top: px(b.top), right: "0", height: px(b.height) },
  ];
});

const HANDLE_POSITIONS: Record<Handle, Record<string, string>> = {
  n: { left: "50%", top: "0", cursor: "ns-resize" },
  s: { left: "50%", top: "100%", cursor: "ns-resize" },
  e: { left: "100%", top: "50%", cursor: "ew-resize" },
  w: { left: "0", top: "50%", cursor: "ew-resize" },
  ne: { left: "100%", top: "0", cursor: "nesw-resize" },
  sw: { left: "0", top: "100%", cursor: "nesw-resize" },
  nw: { left: "0", top: "0", cursor: "nwse-resize" },
  se: { left: "100%", top: "100%", cursor: "nwse-resize" },
};

const handleStyle = (handle: Handle) => ({
  ...HANDLE_POSITIONS[handle],
  transform: "translate(-50%, -50%)",
  cursor: props.disabled ? "default" : HANDLE_POSITIONS[handle].cursor,
});

// --- Region updates --------------------------------------------------------

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const round4 = (n: number) => Math.round(n * 10000) / 10000;

const emitRegion = (region: SubtitleRegion) => {
  const w = clamp(region.w, MIN_REGION_W, 1);
  const h = clamp(region.h, MIN_REGION_H, 1);
  const x = clamp(region.x, 0, 1 - w);
  const y = clamp(region.y, 0, 1 - h);
  emit("update:modelValue", { x: round4(x), y: round4(y), w: round4(w), h: round4(h) });
};

const resetRegion = () => emitRegion({ ...defaultSubtitleRegion(props.orientation) });

/** New rectangle after dragging `mode` by (dx, dy) frame fractions from `start`. */
const applyDrag = (mode: DragMode, start: SubtitleRegion, dx: number, dy: number): SubtitleRegion => {
  if (mode === "move") {
    return { x: clamp(start.x + dx, 0, 1 - start.w), y: clamp(start.y + dy, 0, 1 - start.h), w: start.w, h: start.h };
  }
  let left = start.x;
  let top = start.y;
  let right = start.x + start.w;
  let bottom = start.y + start.h;
  if (mode.includes("w")) left = clamp(start.x + dx, 0, right - MIN_REGION_W);
  if (mode.includes("e")) right = clamp(right + dx, left + MIN_REGION_W, 1);
  if (mode.includes("n")) top = clamp(start.y + dy, 0, bottom - MIN_REGION_H);
  if (mode.includes("s")) bottom = clamp(bottom + dy, top + MIN_REGION_H, 1);
  return { x: left, y: top, w: right - left, h: bottom - top };
};

let drag: { mode: DragMode; pointerId: number; startX: number; startY: number; start: SubtitleRegion } | null = null;

const onPointerDown = (event: PointerEvent, mode: DragMode) => {
  if (props.disabled || !rect.value || !boxRef.value) return;
  event.preventDefault();
  boxRef.value.setPointerCapture(event.pointerId);
  boxRef.value.focus({ preventScroll: true });
  drag = { mode, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, start: { ...props.modelValue } };
};

const onPointerMove = (event: PointerEvent) => {
  if (!drag || event.pointerId !== drag.pointerId || !rect.value) return;
  const dx = (event.clientX - drag.startX) / rect.value.w;
  const dy = (event.clientY - drag.startY) / rect.value.h;
  emitRegion(applyDrag(drag.mode, drag.start, dx, dy));
};

const onPointerUp = (event: PointerEvent) => {
  if (!drag || event.pointerId !== drag.pointerId) return;
  try {
    boxRef.value?.releasePointerCapture(event.pointerId);
  } catch {
    // Capture already released (pointercancel).
  }
  drag = null;
};

// Arrow keys nudge the box by 1% (Shift: 5%); Alt + arrows resize instead.
// Directions are visual: the picture never mirrors, so neither do these.
const onKeydown = (event: KeyboardEvent) => {
  if (props.disabled) return;
  const step = event.shiftKey ? 0.05 : 0.01;
  const region = { ...props.modelValue };
  switch (event.key) {
    case "ArrowLeft":
      if (event.altKey) region.w -= step;
      else region.x -= step;
      break;
    case "ArrowRight":
      if (event.altKey) region.w += step;
      else region.x += step;
      break;
    case "ArrowUp":
      if (event.altKey) region.h -= step;
      else region.y -= step;
      break;
    case "ArrowDown":
      if (event.altKey) region.h += step;
      else region.y += step;
      break;
    default:
      return;
  }
  event.preventDefault();
  emitRegion(region);
};

// Numeric fields mirror the model in whole percent; commit on blur/Enter.
const drafts = reactive<Record<FieldKey, string>>({ x: "", y: "", w: "", h: "" });
const toPercent = (fraction: number) => `${Math.round(fraction * 100)}`;

watch(
  () => props.modelValue,
  (region) => {
    drafts.x = toPercent(region.x);
    drafts.y = toPercent(region.y);
    drafts.w = toPercent(region.w);
    drafts.h = toPercent(region.h);
  },
  { immediate: true, deep: true }
);

const commitDraft = (key: FieldKey) => {
  const parsed = Number(drafts[key]);
  if (!Number.isFinite(parsed)) {
    drafts[key] = toPercent(props.modelValue[key]);
    return;
  }
  emitRegion({ ...props.modelValue, [key]: clamp(parsed, 0, 100) / 100 });
};

const coordsText = computed(() =>
  t("pages.hardsubToSubtitle.regionCoords", {
    left: toPercent(props.modelValue.x),
    top: toPercent(props.modelValue.y),
    width: toPercent(props.modelValue.w),
    height: toPercent(props.modelValue.h),
  })
);

// --- Crop preview ----------------------------------------------------------

let previewFrame = 0;

const drawPreview = () => {
  previewFrame = 0;
  const video = videoRef.value;
  const canvas = canvasRef.value;
  if (!video || !canvas || !videoSize.value.w || !videoSize.value.h || video.readyState < 2) return;
  const crop = regionToPixels(props.modelValue, videoSize.value.w, videoSize.value.h);
  if (crop.w < 2 || crop.h < 2) return;
  const scale = Math.min(1, PREVIEW_MAX_WIDTH / crop.w);
  const width = Math.max(1, Math.round(crop.w * scale));
  const height = Math.max(1, Math.round(crop.h * scale));
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return;
  try {
    context.drawImage(video, crop.x, crop.y, crop.w, crop.h, 0, 0, width, height);
  } catch {
    // Frame not decodable yet; the next timeupdate/seeked retries.
  }
};

// Coalesce bursts (drag + timeupdate) into one draw per animation frame.
const requestPreview = () => {
  if (previewFrame || !import.meta.client) return;
  previewFrame = requestAnimationFrame(drawPreview);
};

watch(() => props.modelValue, requestPreview, { deep: true });
watch(videoSize, () => nextTick(measure));
</script>
